import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import JoditEditor from 'jodit-react';
import apiService from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import MultiSelect from '../../components/MultiSelect';
import ConfirmModal from '../../components/ConfirmModal';

const EditPackage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [showBackModal, setShowBackModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || '';
  const aboutEditor = useRef(null);
  
  const sharingConfig = [
    { key: 'quad', label: 'Quad Sharing' },
    { key: 'triple', label: 'Triple Sharing' },
    { key: 'double', label: 'Double Sharing' }
  ];
  
  const hotelCategoryConfig = [
    { key: 'deluxe', label: 'Deluxe' },
    { key: 'star2', label: '2 Star' },
    { key: 'star3', label: '3 Star' },
    { key: 'star4', label: '4 Star' },
    { key: 'star5', label: '5 Star' },
    { key: 'luxury', label: 'Luxury' }
  ];

  const [formData, setFormData] = useState({
    id: '', name: '', slug: '', countryId: '', stateId: [], location: [], pickup: '', drop: '',
    facilityId: [], categoryId: [], currency: '', price: '', months: [], duration: '',
    gstInclude: true, faq: [], inclusions: [''], exclusions: [''],
    metaTitle: '', metaDescription: '', metaKeyword: '', metaScript: '', discountPrice: '', termsAndCondition: '',
    hotelCategoryPrices: { deluxe: '', star2: '', star3: '', star4: '', star5: '', luxury: '' },
    sharing: {
      quad: { price: '', discountPrice: '' },
      triple: { price: '', discountPrice: '' },
      double: { price: '', discountPrice: '' }
    },
    tripType: ''
  });

  const [about, setAbout] = useState('');
  const [keywordLinks, setKeywordLinks] = useState([{ keyword: '', link: '' }]);
  const [itinerary, setItinerary] = useState([]);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [existingGallery, setExistingGallery] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [currencySearch, setCurrencySearch] = useState('');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const countryDropdownRef = useRef(null);
  const currencyDropdownRef = useRef(null);

  const monthsList = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const normalizeSharing = (value) => {
    const fallback = {
      quad: { price: '', discountPrice: '' },
      triple: { price: '', discountPrice: '' },
      double: { price: '', discountPrice: '' }
    };

    if (!value) return fallback;

    let raw = value;
    if (typeof value === 'string') {
      try {
        raw = JSON.parse(value);
      } catch (error) {
        return fallback;
      }
    }

    if (!raw || typeof raw !== 'object') return fallback;

    const quadValue = raw.quad || raw.quard || raw.quadSharing || {};
    const tripleValue = raw.triple || raw.tripleSharing || {};
    const doubleValue = raw.double || raw.doubleSharing || {};

    return {
      quad: {
        price: quadValue.price ?? '',
        discountPrice: quadValue.discountPrice ?? ''
      },
      triple: {
        price: tripleValue.price ?? '',
        discountPrice: tripleValue.discountPrice ?? ''
      },
      double: {
        price: doubleValue.price ?? '',
        discountPrice: doubleValue.discountPrice ?? ''
      }
    };
  };

  const editorConfig = useMemo(() => ({
    readonly: false,
    height: 300,
    uploader: { insertImageAsBase64URI: true },
    removeButtons: ['video', 'file', 'speechRecognize']
  }), []);

  useEffect(() => {
    fetchPackageData();

    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
      setShowBackModal(true);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) setShowCountryDropdown(false);
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target)) setShowCurrencyDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPackageData = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`editPackage/${id}`);
      if (response.status) {
        const data = response.data.data;
        
        setFormData({
          id: data._id, name: data.name || '', slug: data.slug || '', countryId: data.countryId || '',
          stateId: data.stateId || [], location: data.location || [], pickup: data.pickup || '',
          drop: data.drop || '', facilityId: data.facilityId || [], categoryId: data.categoryId || [],
          currency: data.currency || '', price: data.price || '', discountPrice: data.discountPrice || '', months: data.months || [],
          duration: data.duration || '', gstInclude: data.gstInclude !== undefined ? data.gstInclude : true,
          faq: data.faq?.length > 0 ? data.faq : [{ question: '', answer: '' }],
          inclusions: data.inclusions?.length > 0 ? data.inclusions : [''],
          exclusions: data.exclusions?.length > 0 ? data.exclusions : [''],
          metaTitle: data.metaTitle || '',
          metaDescription: data.metaDescription || '',
          metaKeyword: data.metaKeyword || '',
          metaScript: data.metaScript || '',
          termsAndCondition: data.termsAndCondition || '',
          hotelCategoryPrices: data.hotelCategoryPrices
            ? {
                deluxe: data.hotelCategoryPrices.deluxe ?? '',
                star2: data.hotelCategoryPrices.star2 ?? '',
                star3: data.hotelCategoryPrices.star3 ?? '',
                star4: data.hotelCategoryPrices.star4 ?? '',
                star5: data.hotelCategoryPrices.star5 ?? '',
                luxury: data.hotelCategoryPrices.luxury ?? ''
              }
            : { deluxe: '', star2: '', star3: '', star4: '', star5: '', luxury: '' },
          sharing: normalizeSharing(data.sharing || data.sharingPrices || data.sharingPrice || data.occupancyPricing),
          tripType: data.tripType || ''
        });
        const kw = data.keywordLink && data.keywordLink.length > 0
          ? data.keywordLink.map(k => ({ keyword: k.keyword || '', link: k.link || '' }))
          : [{ keyword: '', link: '' }];
        setKeywordLinks(kw);
        setAbout(data.about || '');
        setItinerary(generateItineraryFromDuration(data.duration, data.itinerary));
        setImagePreview(data.image ? `${MEDIA_URL}${data.image}` : '');
        setExistingGallery(data.gallery || []);
        setCountries(response.data.country || []);
        setStates(response.data.states || []);
        setLocations(response.data.location || []);
        setFacilities(response.data.facility || []);
        setCategories(response.data.category || []);
        setCurrencies(response.data.currency || []);
        setFaqs(response.data.faq || []);
        
        const selectedCountry = response.data.country?.find(c => c._id === data.countryId);
        if (selectedCountry) setCountrySearch(selectedCountry.name);
        
        const selectedCurrency = response.data.currency?.find(c => c._id === data.currency);
        if (selectedCurrency) setCurrencySearch(selectedCurrency.name);
      }
    } catch (error) {
      toast.error('Failed to load package data');
    } finally {
      setLoading(false);
    }
  };

  const addKeywordLink = () => setKeywordLinks([...keywordLinks, { keyword: '', link: '' }]);
  const removeKeywordLink = (index) => setKeywordLinks(keywordLinks.filter((_, i) => i !== index));
  const updateKeywordLink = (index, field, value) => {
    const updated = [...keywordLinks];
    updated[index][field] = value;
    setKeywordLinks(updated);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be 5MB or less');
        e.target.value = '';
        return;
      }
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const previews = [];

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`File "${file.name}" is too large (max 5MB)`);
        return;
      }
      validFiles.push(file);
      previews.push(URL.createObjectURL(file));
    });

    setGalleryImages([...galleryImages, ...validFiles]);
    setGalleryPreviews([...galleryPreviews, ...previews]);
  };

  const removeGalleryPreview = (index) => {
    setGalleryImages(galleryImages.filter((_, i) => i !== index));
    setGalleryPreviews(galleryPreviews.filter((_, i) => i !== index));
  };

  const deleteExistingImage = async (imageName, index) => {
    try {
      const response = await apiService.post('deleteImage', { type: 'package', typeId: id, imageName });
      if (response.status) {
        setExistingGallery(existingGallery.filter((_, i) => i !== index));
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleCountryChange = async (country) => {
    const newFormData = { ...formData, countryId: country._id, stateId: [], location: [] };
    setFormData(newFormData);
    setCountrySearch(country.name);
    setShowCountryDropdown(false);
    try {
      const response = await apiService.post('stateLocationList', { countryId: country._id, stateId: [], type: 'country' });
      if (response.message === 'success') {
        setStates(response.data.states || []);
        setLocations(response.data.locations || []);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const handleStateChange = async (stateIds) => {
    if (stateIds.length > 0) {
      try {
        const response = await apiService.post('stateLocationList', { countryId: formData.countryId, stateId: stateIds, type: 'state' });
        if (response.message === 'success') {
          setLocations(response.data.locations || []);
        }
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    } else {
      setLocations([]);
    }
  };

  const handleAddLocation = () => {
    if (!formData.countryId) {
      toast.error('Select country to add location!!');
      return;
    }
    if (formData.stateId.length === 0) {
      toast.error('Select State/Region to add location!!');
      return;
    }
    setShowLocationModal(true);
  };

  const handleLocationSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await apiService.post('addVendorLocation', { name: newLocationName, countryId: formData.countryId, stateId: formData.stateId });
      if (!response.success) {
        toast.success(response.message);
        setLocations([...locations, response.data]);
        setFormData({ ...formData, location: [...formData.location, response.data._id] });
        setShowLocationModal(false);
        setNewLocationName('');
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    }
  };

  const generateItineraryFromDuration = (duration, existingItinerary = []) => {
    if (!duration) return existingItinerary || [];
    const days = parseInt(duration.split(' ')[0]);
    const itineraryArray = Array(days).fill('');
    if (existingItinerary && existingItinerary.length > 0) {
      for (let i = 0; i < Math.min(days, existingItinerary.length); i++) {
        itineraryArray[i] = existingItinerary[i] || '';
      }
    }
    return itineraryArray;
  };

  const handleDurationChange = (e) => {
    const duration = e.target.value;
    setFormData({ ...formData, duration });
    if (duration) {
      const days = parseInt(duration.split(' ')[0]);
      const currentItinerary = itinerary;
      if (days > currentItinerary.length) {
        const newItinerary = [...currentItinerary, ...Array(days - currentItinerary.length).fill('')];
        setItinerary(newItinerary);
      } else if (days < currentItinerary.length) {
        setItinerary(currentItinerary.slice(0, days));
      }
    } else {
      setItinerary([]);
    }
  };

  const addField = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const removeField = (field, index) => {
    setFormData({ ...formData, [field]: formData[field].filter((_, i) => i !== index) });
  };

  const handleFieldChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const filteredCountries = countries.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()));
  const filteredCurrencies = currencies.filter(c => c.name.toLowerCase().includes(currencySearch.toLowerCase()));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (existingGallery.length + galleryImages.length > 10) {
      toast.error('You can upload maximum 10 images!!');
      return;
    }

    try {
      setSubmitting(true);
      const data = new FormData();
      
      data.append('id', formData.id);
      data.append('name', formData.name);
      data.append('slug', formData.slug || '');
      data.append('countryId', formData.countryId);
      formData.stateId.forEach(s => data.append('stateId[]', s));
      formData.location.forEach(l => data.append('location[]', l));
      data.append('pickup', formData.pickup);
      data.append('drop', formData.drop);
      formData.facilityId.forEach(f => data.append('facilityId[]', f));
      formData.categoryId.forEach(c => data.append('categoryId[]', c));
      data.append('currency', formData.currency);
      data.append('price', formData.price);
      data.append('discountPrice', formData.discountPrice || '');
      formData.months.forEach(m => data.append('months[]', m));
      data.append('duration', formData.duration);
      data.append('gstInclude', formData.gstInclude);
      data.append('about', about);
      data.append('termsAndCondition', formData.termsAndCondition || '');
      data.append('metaTitle', formData.metaTitle || '');
      data.append('metaDescription', formData.metaDescription || '');
      data.append('metaKeyword', formData.metaKeyword || '');
      data.append('metaScript', formData.metaScript || '');
      itinerary.forEach(i => data.append('itinerary[]', i));
      formData.faq.filter(f => f.question && f.answer).forEach(f => {
        data.append('faq[]', JSON.stringify(f));
      });
      formData.inclusions.forEach(i => data.append('inclusions[]', i));
      formData.exclusions.forEach(e => data.append('exclusions[]', e));

      const filteredKeywordLinks = keywordLinks.filter(k => k.keyword?.trim() || k.link?.trim());
      const deleteAllKeywordLink = filteredKeywordLinks.length === 0 ? '1' : '0';
      
      filteredKeywordLinks.forEach((k, idx) => {
        data.append(`keywordLink[${idx}][keyword]`, k.keyword || '');
        data.append(`keywordLink[${idx}][link]`, k.link || '');
      });
      data.append('delete_all_keywordLink', deleteAllKeywordLink);

      data.append('hotelCategoryPrices', JSON.stringify(formData.hotelCategoryPrices));
      data.append('sharing', JSON.stringify(formData.sharing));
      data.append('tripType', formData.tripType || '');

      if (image) data.append('image', image);
      galleryImages.forEach(img => data.append('images', img));

      const response = await apiService.postFormData('updatePackage', data);
      if (response.status) {
        toast.success(response.message);
        navigate('/view-packages');
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: 'bi-info-circle' },
    { id: 'details', label: 'Description & Terms', icon: 'bi-file-text' },
    { id: 'pricing', label: 'Pricing & Sharing', icon: 'bi-currency-dollar' },
    { id: 'itinerary', label: 'Itinerary', icon: 'bi-calendar-event' },
    { id: 'highlights', label: 'Highlights & FAQ', icon: 'bi-list-check' },
    { id: 'seo', label: 'SEO & Gallery', icon: 'bi-search' },
    { id: 'keywordLink', label: 'Keyword Link', icon: 'bi-link-45deg' }
  ];

  return (
    <div className="p-0">
      <div className="flex items-center justify-between mb-6">
        <div>
          <nav className="text-sm flex items-center mb-2">
            <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
            <span className="mx-2 text-gray-400">/</span>
            <Link to="/view-packages" className="text-gray-500 hover:text-gray-700">Packages</Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-900 font-medium">Edit Package</span>
          </nav>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <i className="bi bi-box-seam mr-3 text-[#009ef7]"></i>
            Edit Package {formData.name && `- ${formData.name}`}
          </h1>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#009ef7]"></div>
          <span className="ml-3 text-gray-600">Loading...</span>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="p-0">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            {/* Tab navigation */}
            <div className="flex border-b border-gray-200 overflow-x-auto bg-gray-50/50 rounded-t-lg">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-3.5 font-semibold text-sm transition-all whitespace-nowrap border-b-2 ${
                    activeTab === tab.id
                      ? 'border-[#009ef7] text-[#009ef7] bg-white'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <i className={`bi ${tab.icon}`}></i>
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-6">
              {activeTab === 'basic' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Image Preview Box */}
                    <div className="lg:col-span-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Package Cover Image</label>
                      <div className="relative w-full h-64 border border-gray-200 rounded-xl overflow-hidden bg-slate-50 flex items-center justify-center group shadow-sm transition-all hover:border-gray-300">
                        {imagePreview ? (
                          <>
                            <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                              <span className="text-white text-xs font-medium px-3 py-1.5 bg-black/60 rounded-full">Change Cover Image</span>
                            </div>
                          </>
                        ) : (
                          <div className="text-center p-4">
                            <i className="bi bi-cloud-arrow-up text-4xl text-gray-400 block mb-2"></i>
                            <span className="text-sm text-gray-500 font-medium">Click to upload image</span>
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2 text-center">Allowed: png, jpg, jpeg (Max 5MB)</p>
                    </div>

                    {/* Standard inputs block */}
                    <div className="lg:col-span-8 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Package Name <span className="text-red-500">*</span></label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                            placeholder="Enter package name"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Slug <span className="text-gray-400 text-xs font-normal">(Editable URL identifier)</span></label>
                          <input
                            type="text"
                            name="slug"
                            value={formData.slug}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                            placeholder="e.g. package-slug-identifier"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Country <span className="text-red-500">*</span></label>
                          <div className="relative" ref={countryDropdownRef}>
                            <input
                              type="text"
                              value={countrySearch}
                              onChange={(e) => { setCountrySearch(e.target.value); setShowCountryDropdown(true); }}
                              onFocus={() => setShowCountryDropdown(true)}
                              placeholder="Search Country"
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                              required={!formData.countryId}
                            />
                            {showCountryDropdown && (
                              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                {filteredCountries.length > 0 ? filteredCountries.map(c => (
                                  <div
                                    key={c._id}
                                    onClick={() => handleCountryChange(c)}
                                    className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer text-sm transition-colors"
                                  >
                                    {c.name}
                                  </div>
                                )) : (
                                  <div className="px-4 py-2.5 text-gray-500 text-sm text-center">No countries found</div>
                                )}
                              </div>
                            )}
                          </div>
                          <input type="hidden" name="countryId" value={formData.countryId} required />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Region / State <span className="text-red-500">*</span></label>
                          <MultiSelect
                            key={`states-${formData.countryId}`}
                            options={states}
                            value={formData.stateId}
                            onChange={(value) => { 
                              const newFormData = { ...formData, stateId: value, location: [] };
                              setFormData(newFormData);
                              handleStateChange(value);
                            }}
                            placeholder="Select State/Region"
                          />
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Locations Covered <span className="text-red-500">*</span></label>
                          <MultiSelect
                            key={`locations-${formData.stateId.join('-')}`}
                            options={locations}
                            value={formData.location}
                            onChange={(value) => setFormData({ ...formData, location: value })}
                            placeholder="Select Locations"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAddLocation}
                          className="px-4 py-2.5 text-white rounded-lg self-end flex items-center gap-1.5 text-sm font-semibold hover:opacity-95 transition-opacity"
                          style={{ backgroundColor: '#009ef7', height: '42px' }}
                        >
                          <i className="bi bi-plus-lg"></i> Location
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 border-t pt-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Pickup Location</label>
                      <input type="text" name="pickup" value={formData.pickup} onChange={handleInputChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white" placeholder="Pickup point" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Drop Location</label>
                      <input type="text" name="drop" value={formData.drop} onChange={handleInputChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white" placeholder="Drop point" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Trip Type</label>
                      <select name="tripType" value={formData.tripType} onChange={handleInputChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white">
                        <option value="">Select trip type</option>
                        <option value="Group Trip">Group Trip</option>
                        <option value="Private Trip">Private Trip</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">GST (Include)</label>
                      <div className="flex items-center gap-6 h-[42px]">
                        <label className="flex items-center text-sm text-gray-700 cursor-pointer">
                          <input type="radio" name="gstInclude" checked={formData.gstInclude === true} onChange={() => setFormData({ ...formData, gstInclude: true })} className="mr-2 h-4 w-4 text-[#009ef7] focus:ring-[#009ef7]" />
                          Yes
                        </label>
                        <label className="flex items-center text-sm text-gray-700 cursor-pointer">
                          <input type="radio" name="gstInclude" checked={formData.gstInclude === false} onChange={() => setFormData({ ...formData, gstInclude: false })} className="mr-2 h-4 w-4 text-[#009ef7] focus:ring-[#009ef7]" />
                          No
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 border-t pt-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Travel Facility <span className="text-red-500">*</span></label>
                      <MultiSelect
                        options={facilities}
                        value={formData.facilityId}
                        onChange={(value) => setFormData({ ...formData, facilityId: value })}
                        placeholder="Select Facilities"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Package Category <span className="text-red-500">*</span></label>
                      <MultiSelect
                        options={categories}
                        value={formData.categoryId}
                        onChange={(value) => setFormData({ ...formData, categoryId: value })}
                        placeholder="Select Categories"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Months Recommended</label>
                      <MultiSelect
                        options={monthsList.map(month => ({ _id: month, name: month }))}
                        value={formData.months}
                        onChange={(value) => setFormData({ ...formData, months: value })}
                        placeholder="Select Months"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 border-t pt-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Currency <span className="text-red-500">*</span></label>
                      <div className="relative" ref={currencyDropdownRef}>
                        <input
                          type="text"
                          value={currencySearch}
                          onChange={(e) => { setCurrencySearch(e.target.value); setShowCurrencyDropdown(true); }}
                          onFocus={() => setShowCurrencyDropdown(true)}
                          placeholder="Currency"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                          required={!formData.currency}
                        />
                        {showCurrencyDropdown && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {filteredCurrencies.length > 0 ? filteredCurrencies.map(c => (
                              <div
                                key={c._id}
                                onClick={() => { setFormData({ ...formData, currency: c._id }); setCurrencySearch(c.name); setShowCurrencyDropdown(false); }}
                                className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer text-sm transition-colors"
                              >
                                {c.name}
                              </div>
                            )) : (
                              <div className="px-4 py-2.5 text-gray-500 text-sm text-center">No currencies found</div>
                            )}
                          </div>
                        )}
                      </div>
                      <input type="hidden" name="currency" value={formData.currency} required />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Base Price <span className="text-red-500">*</span></label>
                      <input type="number" name="price" value={formData.price} onChange={handleInputChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white" placeholder="Base Price" min="1" required />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Discount Price</label>
                      <input type="number" name="discountPrice" value={formData.discountPrice} onChange={handleInputChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white" placeholder="Discount Price" min="0" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Duration <span className="text-red-500">*</span></label>
                      <select name="duration" value={formData.duration} onChange={handleDurationChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white" required>
                        <option value="">Select duration</option>
                        {Array.from({ length: 29 }, (_, i) => i + 1).map(i => (
                          <option key={i} value={`${i + 1} Days / ${i} ${i === 1 ? 'Night' : 'Nights'}`}>
                            {i + 1} Days / {i} {i === 1 ? 'Night' : 'Nights'}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'details' && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">About this Package</label>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <JoditEditor ref={aboutEditor} value={about} config={editorConfig} onBlur={newContent => setAbout(newContent)} />
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Terms and Conditions</label>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <JoditEditor
                        value={formData.termsAndCondition}
                        config={editorConfig}
                        onBlur={newContent => setFormData({ ...formData, termsAndCondition: newContent })}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'pricing' && (
                <div className="space-y-8">
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1">Hotel Category Prices</h3>
                    <p className="text-xs text-gray-500 mb-4">Set category specific pricing addons</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                      {hotelCategoryConfig.map((item) => (
                        <div key={item.key} className="bg-slate-50 p-3 rounded-lg border border-gray-200">
                          <label className="block text-xs font-semibold text-gray-700 mb-1.5">{item.label}</label>
                          <input
                            type="number"
                            value={formData.hotelCategoryPrices[item.key]}
                            onChange={(e) => setFormData({ ...formData, hotelCategoryPrices: { ...formData.hotelCategoryPrices, [item.key]: e.target.value } })}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                            placeholder="Price"
                            min="0"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="text-base font-bold text-gray-900 mb-1">Occupancy Sharing Prices</h3>
                    <p className="text-xs text-gray-500 mb-4">Configure sharing level parameters</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {sharingConfig.map((item) => (
                        <div key={item.key} className="bg-slate-50 p-4 rounded-xl border border-gray-200 shadow-sm">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 rounded-full bg-[#009ef7]"></div>
                            <h4 className="font-semibold text-gray-900 text-sm">{item.label}</h4>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-600 mb-1">Price</label>
                              <input
                                type="number"
                                value={formData.sharing?.[item.key]?.price ?? ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  sharing: {
                                    ...formData.sharing,
                                    [item.key]: {
                                      ...formData.sharing[item.key],
                                      price: e.target.value
                                    }
                                  }
                                })}
                                className="w-full px-3.5 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                                placeholder="Base Price"
                                min="0"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-semibold text-gray-600 mb-1">Discount Price</label>
                              <input
                                type="number"
                                value={formData.sharing?.[item.key]?.discountPrice ?? ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  sharing: {
                                    ...formData.sharing,
                                    [item.key]: {
                                      ...formData.sharing[item.key],
                                      discountPrice: e.target.value
                                    }
                                  }
                                })}
                                className="w-full px-3.5 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                                placeholder="Discount Price"
                                min="0"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'itinerary' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-base font-bold text-gray-900">Day-wise Details</h3>
                      <p className="text-xs text-gray-500">Provide itinerary logs for selected duration</p>
                    </div>
                  </div>
                  {itinerary.length > 0 ? (
                    <div className="space-y-6">
                      {itinerary.map((item, index) => (
                        <div key={index} className="bg-slate-50/50 p-4 border rounded-xl shadow-sm">
                          <label className="block text-sm font-semibold text-gray-800 mb-3">Day {index + 1} Plan <span className="text-red-500">*</span></label>
                          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                            <JoditEditor
                              value={item}
                              config={editorConfig}
                              onBlur={newContent => {
                                const newIt = [...itinerary];
                                newIt[index] = newContent;
                                setItinerary(newIt);
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 border border-dashed rounded-xl bg-slate-50">
                      <i className="bi bi-calendar-x text-4xl text-gray-300 mb-2"></i>
                      <h4 className="text-sm font-bold text-gray-700">No Duration Selected</h4>
                      <p className="text-xs text-gray-500 mt-1">Please select a package duration under Basic Info to generate day-wise plans.</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'highlights' && (
                <div className="space-y-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Inclusions */}
                    <div className="bg-slate-50 p-4 rounded-xl border border-gray-200">
                      <div className="flex justify-between items-center mb-3">
                        <label className="block text-sm font-bold text-gray-900">Inclusions</label>
                        <button
                          type="button"
                          onClick={() => addField('inclusions')}
                          className="px-2.5 py-1.5 text-xs text-white rounded bg-emerald-500 hover:bg-emerald-600 transition-colors flex items-center gap-1 font-semibold"
                        >
                          <i className="bi bi-plus-circle"></i> Add Include
                        </button>
                      </div>
                      <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                        {formData.inclusions.map((item, index) => (
                          <div key={index} className="flex gap-2">
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => handleFieldChange('inclusions', index, e.target.value)}
                              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200"
                              placeholder="Describe inclusion item..."
                            />
                            <button
                              type="button"
                              onClick={() => removeField('inclusions', index)}
                              className="w-8 h-8 flex items-center justify-center rounded text-red-500 hover:bg-red-50"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Exclusions */}
                    <div className="bg-slate-50 p-4 rounded-xl border border-gray-200">
                      <div className="flex justify-between items-center mb-3">
                        <label className="block text-sm font-bold text-gray-900">Exclusions</label>
                        <button
                          type="button"
                          onClick={() => addField('exclusions')}
                          className="px-2.5 py-1.5 text-xs text-white rounded bg-red-500 hover:bg-red-600 transition-colors flex items-center gap-1 font-semibold"
                        >
                          <i className="bi bi-plus-circle"></i> Add Exclude
                        </button>
                      </div>
                      <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                        {formData.exclusions.map((item, index) => (
                          <div key={index} className="flex gap-2">
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => handleFieldChange('exclusions', index, e.target.value)}
                              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200"
                              placeholder="Describe exclusion item..."
                            />
                            <button
                              type="button"
                              onClick={() => removeField('exclusions', index)}
                              className="w-8 h-8 flex items-center justify-center rounded text-red-500 hover:bg-red-50"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h3 className="text-base font-bold text-gray-900">Frequently Asked Questions</h3>
                        <p className="text-xs text-gray-500">Provide user help queries and answers</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, faq: [...formData.faq, { question: '', answer: '' }] })}
                        className="px-3 py-2 text-white bg-[#009ef7] hover:bg-[#007acc] rounded-lg text-xs font-semibold flex items-center gap-1"
                      >
                        <i className="bi bi-plus-lg"></i> Add FAQ
                      </button>
                    </div>
                    
                    <div className="space-y-4">
                      {formData.faq.map((item, index) => (
                        <div key={index} className="bg-slate-50 p-4 border border-gray-200 rounded-xl relative shadow-sm">
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-xs font-bold text-[#009ef7] uppercase tracking-wider">FAQ Item #{index + 1}</span>
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, faq: formData.faq.filter((_, i) => i !== index) })}
                              className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                            >
                              <i className="bi bi-trash mr-1"></i> Remove
                            </button>
                          </div>
                          <div className="grid grid-cols-1 gap-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Question</label>
                              <input
                                type="text"
                                value={item.question}
                                onChange={(e) => {
                                  const newFaq = [...formData.faq];
                                  newFaq[index].question = e.target.value;
                                  setFormData({ ...formData, faq: newFaq });
                                }}
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                                placeholder="Enter question"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Answer</label>
                              <textarea
                                value={item.answer}
                                onChange={(e) => {
                                  const newFaq = [...formData.faq];
                                  newFaq[index].answer = e.target.value;
                                  setFormData({ ...formData, faq: newFaq });
                                }}
                                rows="3"
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                                placeholder="Enter answer"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'seo' && (
                <div className="space-y-8">
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1">SEO Metadata</h3>
                    <p className="text-xs text-gray-500 mb-4">Optimize your search engine presence</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Meta Title</label>
                        <input type="text" name="metaTitle" value={formData.metaTitle} onChange={handleInputChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white" placeholder="Enter meta title" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Meta Keywords</label>
                        <input type="text" name="metaKeyword" value={formData.metaKeyword} onChange={handleInputChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white" placeholder="keyword1, keyword2" />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Meta Description</label>
                      <textarea name="metaDescription" value={formData.metaDescription} onChange={handleInputChange} rows="3" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white" placeholder="Enter meta description" />
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Meta Script</label>
                      <textarea name="metaScript" value={formData.metaScript} onChange={handleInputChange} rows="3" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white" placeholder="Add meta script (optional)" />
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="text-base font-bold text-gray-900 mb-1">Gallery Uploads</h3>
                    <p className="text-xs text-gray-500 mb-4">Manage multiple display gallery images (max 10 images total)</p>
                    
                    <div className="mb-4">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleGalleryChange}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm file:mr-4 file:py-1.5 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-[#009ef7] hover:file:bg-blue-100 cursor-pointer bg-white"
                      />
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                      {existingGallery.map((img, index) => (
                        <div key={`existing-${index}`} className="relative group aspect-video border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-slate-100">
                          <img src={`${MEDIA_URL}${img}`} alt="" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => deleteExistingImage(img, index)}
                            className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow hover:bg-red-600 font-bold text-xs"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      {galleryPreviews.map((preview, index) => (
                        <div key={`new-${index}`} className="relative group aspect-video border rounded-xl overflow-hidden shadow bg-slate-100 border-[#009ef7]/40">
                          <img src={preview} alt="" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeGalleryPreview(index)}
                            className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow hover:bg-red-600 font-bold text-xs"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'keywordLink' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <h3 className="text-base font-bold text-gray-900">Keyword Links</h3>
                      <p className="text-xs text-gray-500">Manage internal/external keyword URL mappings</p>
                    </div>
                    <button
                      type="button"
                      onClick={addKeywordLink}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors"
                    >
                      <i className="bi bi-plus-circle"></i> Add Row
                    </button>
                  </div>

                  <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Keyword</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Target Link / URL</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider w-16">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {keywordLinks.length > 0 ? (
                          keywordLinks.map((item, index) => (
                            <tr key={index} className="hover:bg-slate-50 transition-colors">
                              <td className="px-4 py-2">
                                <input
                                  type="text"
                                  value={item.keyword}
                                  onChange={(e) => updateKeywordLink(index, 'keyword', e.target.value)}
                                  placeholder="e.g. Travel Guide"
                                  className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                                />
                              </td>
                              <td className="px-4 py-2">
                                <input
                                  type="text"
                                  value={item.link}
                                  onChange={(e) => updateKeywordLink(index, 'link', e.target.value)}
                                  placeholder="e.g. https://example.com/guide"
                                  className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                                />
                              </td>
                              <td className="px-4 py-2 text-center">
                                <button
                                  type="button"
                                  onClick={() => removeKeywordLink(index)}
                                  className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3" className="px-4 py-6 text-center text-gray-400 text-sm">
                              No keyword links added yet. Click "Add Row" to create one.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4 flex justify-between items-center sticky bottom-0 z-10">
            <button
              type="button"
              onClick={() => setShowBackModal(true)}
              className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm transition-all"
            >
              <i className="bi bi-arrow-left mr-2"></i>
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2.5 bg-[#009ef7] text-white rounded-lg hover:bg-[#007acc] disabled:opacity-50 font-medium text-sm shadow-sm transition-all flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle"></i>
                  Save Package
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Add Location</h3>
              <button onClick={() => setShowLocationModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <form onSubmit={handleLocationSubmit}>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Location Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={newLocationName}
                onChange={(e) => setNewLocationName(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none mb-4 bg-white"
                placeholder="Enter Location Name"
                required
              />
              <div className="flex justify-end gap-3 border-t pt-4">
                <button type="button" onClick={() => setShowLocationModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">Cancel</button>
                <button type="submit" className="px-4 py-2 text-white rounded-lg text-sm font-medium" style={{ backgroundColor: '#009ef7' }}>Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showBackModal}
        title="Are you sure you want to go back?"
        message="Any unsaved package changes will be lost."
        confirmText="Yes, Go Back"
        cancelText="Stay Here"
        onConfirm={() => navigate('/view-packages')}
        onCancel={() => setShowBackModal(false)}
      />
    </div>
  );
};

export default EditPackage;
